<script setup>
// "Remind me to follow up": private reminders on this conversation.
// Two visually separate sections: existing reminders (each its own card,
// editing expands INSIDE that card) and a bordered "new reminder" block.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import RemindersAPI from 'dashboard/api/teletel/reminders';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ReminderCompletionDialog from './ReminderCompletionDialog.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: Number, default: null },
  // true: rendered inside the rail side panel (no overlay, no card frame)
  embedded: { type: Boolean, default: false },
});
const emit = defineEmits(['close']);

const { t } = useI18n();

const existing = ref([]);
const isSaving = ref(false);

const note = ref('');
const customAt = ref('');
const preset = ref('tomorrow10');

const editingId = ref(null);
const editAt = ref('');
const editNote = ref('');
const completionTarget = ref(null);
const isFinishing = ref(false);

const presets = [
  { key: 'hour1' },
  { key: 'hours3' },
  { key: 'tomorrow10' },
  { key: 'days3' },
  { key: 'custom' },
];

const presetTime = key => {
  const now = new Date();
  if (key === 'hour1') return new Date(now.getTime() + 3600 * 1000);
  if (key === 'hours3') return new Date(now.getTime() + 3 * 3600 * 1000);
  if (key === 'tomorrow10' || key === 'days3') {
    const d = new Date(now);
    d.setDate(d.getDate() + (key === 'days3' ? 3 : 1));
    d.setHours(10, 0, 0, 0);
    return d;
  }
  return customAt.value ? new Date(customAt.value) : null;
};

const remindAt = computed(() => presetTime(preset.value));
const remindAtLabel = computed(() =>
  remindAt.value ? remindAt.value.toLocaleString() : ''
);
const canSave = computed(
  () => remindAt.value && remindAt.value.getTime() > Date.now()
);

const pad = n => String(n).padStart(2, '0');
const toLocalInput = value => {
  const d = new Date(value);
  const datePart = [d.getFullYear(), pad(d.getMonth() + 1), pad(d.getDate())];
  return `${datePart.join('-')}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const loadExisting = async () => {
  if (!props.conversationId) return;
  try {
    const { data } = await RemindersAPI.forConversation(props.conversationId);
    existing.value = data.payload;
  } catch {
    existing.value = [];
  }
};

watch(
  () => props.show,
  visible => {
    if (visible) {
      editingId.value = null;
      loadExisting();
    }
  }
);

const save = async () => {
  isSaving.value = true;
  try {
    await RemindersAPI.create({
      conversation_display_id: props.conversationId,
      remind_at: remindAt.value.toISOString(),
      note: note.value,
    });
    useAlert(t('TELETEL_REMINDERS.DIALOG.SAVED'));
    note.value = '';
    emitter.emit('teletelReminderChanged');
    loadExisting();
  } catch {
    useAlert(t('TELETEL_REMINDERS.DIALOG.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const startEdit = reminder => {
  editingId.value = reminder.id;
  editAt.value = toLocalInput(reminder.remind_at);
  editNote.value = reminder.note || '';
};

const saveEdit = async () => {
  try {
    await RemindersAPI.updateReminder(editingId.value, {
      remindAt: new Date(editAt.value).toISOString(),
      note: editNote.value,
    });
    useAlert(t('TELETEL_REMINDERS.PAGE.UPDATED'));
    editingId.value = null;
    emitter.emit('teletelReminderChanged');
    loadExisting();
  } catch {
    useAlert(t('TELETEL_REMINDERS.DIALOG.ERROR'));
  }
};

const finish = async (reminder, status, completionReason = null) => {
  isFinishing.value = true;
  try {
    await RemindersAPI.markStatus(reminder.id, status, completionReason);
    completionTarget.value = null;
    emitter.emit('teletelReminderChanged');
    loadExisting();
  } catch {
    useAlert(t('TELETEL_REMINDERS.DIALOG.ERROR'));
  } finally {
    isFinishing.value = false;
  }
};

const completeWithReason = reason => {
  if (!completionTarget.value) return;
  finish(completionTarget.value, 'done', reason);
};

const formatAt = value => new Date(value).toLocaleString();
</script>

<template>
  <div
    v-if="show"
    :class="
      embedded
        ? 'flex flex-col h-full overflow-auto'
        : 'fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'
    "
    @click.self="embedded ? null : emit('close')"
  >
    <div
      :class="
        embedded
          ? 'flex flex-col overflow-auto px-4 pb-4'
          : 'flex max-h-[85vh] w-full max-w-md flex-col overflow-auto rounded-xl border border-n-weak bg-n-solid-1 p-5'
      "
    >
      <h2 v-if="!embedded" class="text-lg font-semibold text-n-slate-12">
        {{ t('TELETEL_REMINDERS.DIALOG.TITLE') }}
      </h2>
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('TELETEL_REMINDERS.DIALOG.PRIVACY') }}
      </p>

      <!-- composer first: the thing an agent opens this panel for
           (owner order 2026-09-16: one layout, aligned controls) -->
      <div class="mt-4 rounded-xl border border-n-weak bg-n-alpha-1 p-3">
        <p
          class="mb-2 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
        >
          <span
            class="i-lucide-alarm-clock-plus size-4 text-n-blue-11"
            aria-hidden="true"
          />
          {{ t('TELETEL_REMINDERS.DIALOG.NEW_SECTION') }}
        </p>
        <div class="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
          <button
            v-for="option in presets"
            :key="option.key"
            type="button"
            class="rounded-lg border px-2 py-1.5 text-xs transition-colors"
            :class="
              preset === option.key
                ? 'border-n-brand bg-n-brand font-medium text-white'
                : 'border-n-weak bg-n-solid-1 text-n-slate-11 hover:border-n-brand'
            "
            @click="preset = option.key"
          >
            {{
              t(`TELETEL_REMINDERS.DIALOG.PRESETS.${option.key.toUpperCase()}`)
            }}
          </button>
        </div>

        <input
          v-if="preset === 'custom'"
          v-model="customAt"
          type="datetime-local"
          class="mt-2 w-full !mb-0"
        />

        <textarea
          v-model="note"
          rows="2"
          class="mt-2 w-full !mb-0"
          :placeholder="t('TELETEL_REMINDERS.DIALOG.NOTE_PLACEHOLDER')"
        />

        <p v-if="remindAtLabel" class="mt-2 mb-0 text-xs text-n-teal-11">
          {{ t('TELETEL_REMINDERS.DIALOG.WILL_FIRE_AT') }} {{ remindAtLabel }}
        </p>
        <NextButton
          sm
          class="mt-2 w-full"
          icon="i-lucide-bell-plus"
          :label="t('TELETEL_REMINDERS.DIALOG.SAVE')"
          :disabled="!canSave"
          :is-loading="isSaving"
          @click="save"
        />
      </div>

      <!-- existing reminders: one card each, the same three icon actions
           in the same order on every card -->
      <div v-if="existing.length" class="mt-4">
        <p class="mb-2 text-sm font-semibold text-n-slate-12">
          {{ t('TELETEL_REMINDERS.DIALOG.EXISTING') }}
          <span class="text-xs font-normal text-n-slate-11"
            >({{ existing.length }})</span
          >
        </p>
        <div class="flex flex-col gap-2">
          <div
            v-for="reminder in existing"
            :key="reminder.id"
            class="rounded-xl border border-n-weak bg-n-solid-1 p-3"
            :class="{
              '!border-n-brand': editingId === reminder.id,
            }"
          >
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <p
                  class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
                >
                  <span
                    class="i-lucide-alarm-clock size-4 shrink-0 text-n-amber-11"
                    aria-hidden="true"
                  />
                  {{ formatAt(reminder.remind_at) }}
                </p>
                <p
                  v-if="reminder.note"
                  class="mt-1 whitespace-pre-wrap break-words text-sm text-n-slate-11"
                >
                  {{ reminder.note }}
                </p>
              </div>
              <div class="flex shrink-0 items-center gap-0.5">
                <NextButton
                  v-tooltip="t('TELETEL_REMINDERS.DIALOG.EDIT')"
                  xs
                  ghost
                  slate
                  icon="i-lucide-pencil"
                  @click="startEdit(reminder)"
                />
                <NextButton
                  v-tooltip="t('TELETEL_REMINDERS.DIALOG.DONE')"
                  xs
                  ghost
                  teal
                  icon="i-lucide-check"
                  @click="completionTarget = reminder"
                />
                <NextButton
                  v-tooltip="t('TELETEL_REMINDERS.DIALOG.DISMISS')"
                  xs
                  ghost
                  ruby
                  icon="i-lucide-x"
                  @click="finish(reminder, 'dismissed')"
                />
              </div>
            </div>

            <!-- inline editor, expands inside this card only -->
            <div
              v-if="editingId === reminder.id"
              class="mt-3 border-t border-n-weak pt-3"
            >
              <input
                v-model="editAt"
                type="datetime-local"
                class="w-full !mb-0"
              />
              <textarea
                v-model="editNote"
                rows="2"
                class="mt-2 w-full !mb-0"
                :placeholder="t('TELETEL_REMINDERS.DIALOG.NOTE_PLACEHOLDER')"
              />
              <div class="mt-2 flex justify-end gap-2">
                <NextButton
                  xs
                  faded
                  slate
                  :label="t('TELETEL_REMINDERS.DIALOG.CANCEL')"
                  @click="editingId = null"
                />
                <NextButton
                  xs
                  :label="t('TELETEL_REMINDERS.DIALOG.UPDATE')"
                  :disabled="!editAt"
                  @click="saveEdit"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <p v-else class="mt-4 text-center text-xs text-n-slate-11">
        {{ t('TELETEL_REMINDERS.DIALOG.EMPTY') }}
      </p>

      <div v-if="!embedded" class="mt-4 flex justify-end">
        <NextButton
          faded
          slate
          :label="t('TELETEL_REMINDERS.DIALOG.CLOSE')"
          @click="emit('close')"
        />
      </div>
    </div>

    <ReminderCompletionDialog
      :show="Boolean(completionTarget)"
      :is-loading="isFinishing"
      @close="completionTarget = null"
      @confirm="completeWithReason"
    />
  </div>
</template>
